import React, { useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import AuthCard from '../components/AuthCard';
import { getAuthService } from '../services';
import { PendingApprovalError } from '../services/types';
import { isSsoEnabled } from '../config/appConfig';
import { useAuth } from '../auth/AuthContext';

interface LoginScreenProps {
    onGoToRegister: () => void;
    onForgotPassword: () => void;
    onMfaRequired: (email: string, password: string) => void;
    onNewPasswordRequired: (email: string) => void;
    onConfirmationRequired: (email: string) => void;
}

export default function LoginScreen({
    onGoToRegister,
    onForgotPassword,
    onMfaRequired,
    onNewPasswordRequired,
    onConfirmationRequired,
}: LoginScreenProps) {
    const { setUser } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        setError(null);
        setNotice(null);
        if (!email.trim() || !password) {
            setError('Enter your email and password.');
            return;
        }
        setSubmitting(true);
        try {
            const result = await getAuthService().signIn(email.trim(), password);
            if (result.status === 'EMAIL_MFA_REQUIRED') {
                onMfaRequired(email.trim(), password);
            } else if (result.status === 'NEW_PASSWORD_REQUIRED') {
                onNewPasswordRequired(email.trim());
            } else if (result.status === 'CONFIRMATION_REQUIRED') {
                onConfirmationRequired(email.trim());
            } else {
                setUser(result.user);
            }
        } catch (e) {
            if (e instanceof PendingApprovalError) {
                setNotice(e.message);
            } else {
                setError(e instanceof Error ? e.message : 'Sign in failed.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AuthCard title="Sign in" subtitle="Access your supplier account">
            <SpaceBetween size="l">
                <FormField label="Email">
                    <Input
                        type="email"
                        value={email}
                        autoComplete
                        onChange={({ detail }) => setEmail(detail.value)}
                    />
                </FormField>
                <FormField label="Password">
                    <Input
                        type="password"
                        value={password}
                        onChange={({ detail }) => setPassword(detail.value)}
                    />
                </FormField>
                {notice ? <Alert type="info">{notice}</Alert> : null}
                {error ? <Alert type="error">{error}</Alert> : null}
                <Button variant="primary" loading={submitting} onClick={handleSubmit}>
                    Sign in
                </Button>
                <SpaceBetween size="xs">
                    <Button variant="link" onClick={onForgotPassword}>
                        Forgot password?
                    </Button>
                    <Button variant="link" onClick={onGoToRegister}>
                        Need an account? Register
                    </Button>
                </SpaceBetween>

                {isSsoEnabled() ? (
                    <Button
                        onClick={() => {
                            getAuthService()
                                .signInWithSso()
                                .catch((e) =>
                                    setError(
                                        e instanceof Error ? e.message : 'SSO sign in failed.',
                                    ),
                                );
                        }}
                    >
                        Amazon employee? Sign in with Midway
                    </Button>
                ) : null}
            </SpaceBetween>
        </AuthCard>
    );
}
